// Cronograma oficial de transição do ICMS/PIS/COFINS para IBS/CBS,
// conforme LC 214/2025. Módulo puro (sem I/O) — recebe os totais tributários
// já apurados de um período e projeta a carga tributária ano a ano.
//
// Percentuais de implementação por ano (% IBS, % CBS), conforme o chamado:
// 2026 (0,1% / 0,9%) · 2027 (0,1% / 100%) · 2028 (0,1% / 100%) ·
// 2029 (10% / 100%) · 2030 (20% / 100%) · 2031 (30% / 100%) ·
// 2032 (40% / 100%) · 2033 (100% / 100%).

export const PRIMEIRO_ANO_TRANSICAO = 2026;
export const ULTIMO_ANO_TRANSICAO = 2033;

export interface PercentualCronogramaAno {
  ano: number;
  percentualIBS: number; // 0-100
  percentualCBS: number; // 0-100
  observacao: string;
}

/** Tabela oficial da LC 214/2025 — não editar sem atualizar o chamado de origem. */
export const CRONOGRAMA_TRANSICAO_LC214: readonly PercentualCronogramaAno[] = [
  {
    ano: 2026,
    percentualIBS: 0.1,
    percentualCBS: 0.9,
    observacao: 'Regime atual — ICMS, PIS, COFINS e IPI integrais',
  },
  {
    ano: 2027,
    percentualIBS: 0.1,
    percentualCBS: 100,
    observacao: 'CBS implementada integralmente; PIS/COFINS extintos',
  },
  { ano: 2028, percentualIBS: 0.1, percentualCBS: 100, observacao: 'Período de adaptação' },
  {
    ano: 2029,
    percentualIBS: 10,
    percentualCBS: 100,
    observacao: 'Início gradual do IBS; ICMS reduzido proporcionalmente',
  },
  { ano: 2030, percentualIBS: 20, percentualCBS: 100, observacao: 'Redução proporcional ICMS' },
  { ano: 2031, percentualIBS: 30, percentualCBS: 100, observacao: 'Redução proporcional ICMS' },
  { ano: 2032, percentualIBS: 40, percentualCBS: 100, observacao: 'Redução proporcional ICMS' },
  {
    ano: 2033,
    percentualIBS: 100,
    percentualCBS: 100,
    observacao: 'Transição completa — IBS/CBS plenos; ICMS extinto',
  },
];

/**
 * Totais tributários reais de um período, usados como ponto de partida da
 * projeção. `pisCofins` (PIS+COFINS combinados) não aparece em nenhuma aba
 * da tela — o chamado só pede ICMS/IPI/IBS/CBS/IS nas abas Situação Atual e
 * Simulação — mas é necessário aqui como base de substituição do CBS (ver
 * `calcularProjecaoAno`).
 */
export interface TotaisBaseProjecao {
  totalVendas: number;
  icms: number;
  ipi: number;
  pisCofins: number;
  ibs: number;
  cbs: number;
  is: number;
}

export interface ValorProjetado {
  valor: number;
  percentualSobreVendas: number;
}

export interface ProjecaoAno {
  ano: number;
  percentualIBS: number;
  percentualCBS: number;
  observacao: string;
  icms: ValorProjetado;
  ipi: ValorProjetado;
  ibs: ValorProjetado;
  cbs: ValorProjetado;
  is: ValorProjetado;
  total: ValorProjetado;
  variacaoAnoAnterior: number; // valor absoluto (R$), positivo = aumento em relação ao ano anterior
}

export interface ProjecaoCompleta {
  anos: ProjecaoAno[];
  cargaAtual: number; // % sobre vendas no primeiro ano do cronograma (2026)
  cargaFinal: number; // % sobre vendas no último ano do cronograma (2033)
  reducaoTotalPontosPercentuais: number; // cargaAtual - cargaFinal, em p.p.
}

function percentualSobreVendas(valor: number, totalVendas: number): number {
  if (!totalVendas) return 0;
  return valor / totalVendas;
}

function valorProjetado(valor: number, totalVendas: number): ValorProjetado {
  return { valor, percentualSobreVendas: percentualSobreVendas(valor, totalVendas) };
}

function buscarPercentuaisAno(ano: number): PercentualCronogramaAno {
  const encontrado = CRONOGRAMA_TRANSICAO_LC214.find(item => item.ano === ano);
  if (!encontrado) {
    throw new Error(
      `Ano ${ano} fora do cronograma de transição da LC 214/2025 (${PRIMEIRO_ANO_TRANSICAO}-${ULTIMO_ANO_TRANSICAO}).`
    );
  }
  return encontrado;
}

/**
 * Projeta a carga tributária de um único ano do cronograma.
 *
 * A LC 214/2025 faz duas substituições independentes, cada uma com sua
 * própria "pizza" que só é redistribuída entre os dois tributos dela — somar
 * tudo numa pizza só faria IBS e CBS dobrarem a base ao mesmo tempo:
 * - **ICMS → IBS**: a pizza é `icms + ibs` (o que hoje é cobrado nesse lado).
 *   `%IBS` do ano define quanto dela vira IBS; o resto continua ICMS.
 * - **PIS/COFINS → CBS**: a pizza é `pisCofins + cbs`. `%CBS` do ano define
 *   quanto dela vira CBS; o resto (PIS/COFINS remanescente) não é exibido em
 *   nenhuma aba, seguindo o escopo do chamado.
 *
 * IPI e IS não têm cronograma de transição na lei — permanecem constantes.
 */
export function calcularProjecaoAno(ano: number, totaisBase: TotaisBaseProjecao): ProjecaoAno {
  const percentuais = buscarPercentuaisAno(ano);
  const { totalVendas, icms, ipi, pisCofins, ibs, cbs, is } = totaisBase;

  const pizzaIcmsIbs = icms + ibs;
  const ibsProjetado = (pizzaIcmsIbs * percentuais.percentualIBS) / 100;
  const icmsProjetado = Math.max(pizzaIcmsIbs - ibsProjetado, 0);

  const pizzaPisCofinsCbs = pisCofins + cbs;
  const cbsProjetado = (pizzaPisCofinsCbs * percentuais.percentualCBS) / 100;

  const totalAno = icmsProjetado + ipi + ibsProjetado + cbsProjetado + is;

  return {
    ano,
    percentualIBS: percentuais.percentualIBS,
    percentualCBS: percentuais.percentualCBS,
    observacao: percentuais.observacao,
    icms: valorProjetado(icmsProjetado, totalVendas),
    ipi: valorProjetado(ipi, totalVendas),
    ibs: valorProjetado(ibsProjetado, totalVendas),
    cbs: valorProjetado(cbsProjetado, totalVendas),
    is: valorProjetado(is, totalVendas),
    total: valorProjetado(totalAno, totalVendas),
    variacaoAnoAnterior: 0, // preenchido em calcularProjecaoCompleta
  };
}

/** Projeta todos os anos de 2026 a 2033, com variação ano a ano e o resumo da transição. */
export function calcularProjecaoCompleta(totaisBase: TotaisBaseProjecao): ProjecaoCompleta {
  const anos: ProjecaoAno[] = [];

  for (const { ano } of CRONOGRAMA_TRANSICAO_LC214) {
    const projecao = calcularProjecaoAno(ano, totaisBase);
    const anterior = anos[anos.length - 1];
    projecao.variacaoAnoAnterior = anterior ? projecao.total.valor - anterior.total.valor : 0;
    anos.push(projecao);
  }

  const primeiro = anos[0];
  const ultimo = anos[anos.length - 1];
  const cargaAtual = primeiro.total.percentualSobreVendas;
  const cargaFinal = ultimo.total.percentualSobreVendas;

  return {
    anos,
    cargaAtual,
    cargaFinal,
    reducaoTotalPontosPercentuais: cargaAtual - cargaFinal,
  };
}
